import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    country: "",
    products_interested: "",
    estimated_volume: "",
    application: "",
    message: "",
};

const BulkInquiry = () => {
    const [form, setForm] = useState(initial);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${API}/inquiries`, form);
            setDone(true);
            setForm(initial);
        } catch (err) {
            setError(
                err?.response?.data?.detail?.[0]?.msg ||
                    "Could not submit inquiry. Please try again or email us directly.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="bulk"
            data-testid="bulk-inquiry-section"
            className="py-24 md:py-36 bg-sun-ink text-sun-bg relative overflow-hidden"
        >
            <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] sun-rays opacity-20 animate-spin-slow pointer-events-none" />
            <div className="absolute top-20 left-20 w-72 h-72 rounded-full bg-sunrise opacity-30 blur-3xl pointer-events-none" />

            <div className="container-x grid grid-cols-12 gap-6 md:gap-12 relative">
                <div className="col-span-12 md:col-span-5 md:sticky md:top-32 self-start">
                    <div className="overline text-sun-goldSoft mb-4">— The Trade Desk</div>
                    <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
                        Buying by
                        <br />
                        the <span className="display-italic text-gradient">tonne?</span>
                    </h2>
                    <p className="mt-6 text-white/70 leading-relaxed max-w-md">
                        Tell us a little about your project. Our trade desk replies within
                        one working day with COA, samples and a CIF/FOB quote.
                    </p>

                    <div className="mt-12 space-y-6 border-t border-white/15 pt-8">
                        <div>
                            <div className="overline text-sun-goldSoft">Trade desk</div>
                            <a
                                href="mailto:trade@ostrichfoods.in"
                                className="block mt-2 font-heading display-italic text-2xl md:text-3xl hover:text-sun-orange transition-colors"
                            >
                                trade@ostrichfoods.in
                            </a>
                        </div>
                        <div>
                            <div className="overline text-sun-goldSoft">Direct line</div>
                            <a
                                href="tel:+919737028999"
                                className="block mt-2 font-heading text-2xl md:text-3xl hover:text-sun-orange transition-colors"
                            >
                                +91 9737 028 999
                            </a>
                        </div>
                    </div>
                </div>

                <div className="col-span-12 md:col-span-7">
                    {done ? (
                        <div
                            data-testid="bulk-inquiry-success"
                            className="bg-white text-sun-ink rounded-3xl p-10 md:p-14"
                        >
                            <div className="w-12 h-12 rounded-full bg-sunrise text-white flex items-center justify-center">
                                <Check className="w-6 h-6" strokeWidth={2} />
                            </div>
                            <h3 className="mt-6 font-heading text-3xl md:text-4xl leading-tight">
                                Inquiry received.
                            </h3>
                            <p className="mt-4 text-sun-inkSoft">
                                Our trade desk will reach out within one working day with
                                samples and pricing. Reference sent to your email.
                            </p>
                            <button
                                onClick={() => setDone(false)}
                                data-testid="bulk-inquiry-reset"
                                className="mt-8 btn-outline"
                            >
                                Submit another
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="bulk-inquiry-form"
                            className="bg-white text-sun-ink rounded-3xl p-6 md:p-10 pb-24 md:pb-10"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                <Field label="Company name *" name="company_name" value={form.company_name} onChange={onChange} required />
                                <Field label="Your name *" name="contact_name" value={form.contact_name} onChange={onChange} required />
                                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} required />
                                <Field label="Phone *" name="phone" value={form.phone} onChange={onChange} required />
                                <Field label="Country / region *" name="country" value={form.country} onChange={onChange} required />
                                <Field label="Volume (kg / month) *" name="estimated_volume" value={form.estimated_volume} onChange={onChange} required />
                                <div className="md:col-span-2">
                                    <Field label="Products of interest *" name="products_interested" value={form.products_interested} onChange={onChange} placeholder="Mango, beetroot, garlic…" required />
                                </div>
                                <div className="md:col-span-2">
                                    <Field label="Application" name="application" value={form.application} onChange={onChange} placeholder="Bakery, nutraceuticals, cosmetics…" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="overline block mb-2">— Brief / message</label>
                                    <textarea
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        data-testid="bulk-input-message"
                                        rows={4}
                                        className="w-full bg-transparent border-0 border-b border-sun-line focus:outline-none focus:border-sun-orange px-0 py-3 font-body text-sun-ink resize-none"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div
                                    data-testid="bulk-inquiry-error"
                                    className="mt-6 border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm rounded-xl"
                                >
                                    {error}
                                </div>
                            )}

                            <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-sun-line">
                                <p className="overline">— We respond within 1 working day.</p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="bulk-inquiry-submit"
                                    className="btn-gradient disabled:opacity-50"
                                >
                                    {submitting ? "Sending…" : "Send inquiry"}
                                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, ...props }) => (
    <div>
        <label className="overline block mb-2">— {label}</label>
        <input
            {...props}
            data-testid={`bulk-input-${props.name}`}
            className="w-full bg-transparent border-0 border-b border-sun-line focus:outline-none focus:border-sun-orange px-0 py-3 font-body text-sun-ink placeholder:text-sun-muted/60"
        />
    </div>
);

export default BulkInquiry;
